import { CuriosityAutocomplete, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorDate,
  ValueEditorDateRange,
  ValueEditorInput,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
  ValueEditorSelect,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SupplierFilterField } from '@/generated/gql/graphql';
import { SupplierQueryFieldGroup, PrequalificationLevelSelections } from '~/common/types';
import {
  queryFieldFactory,
  UseDataGridQueryBuilderConfig,
  operators,
  listOperators,
  renderPercentageEditor,
} from '~/components/DataGridQueryBuilder';
import { LocationValueAutocomplete } from '~/components/LocationValueAutocomplete/LocationValueAutocomplete';
import { ValueEditorSelectAutocomplete } from '~/components/ValueEditorSelectAutocomplete/ValueEditorSelectAutocomplete';
import { ValueEditorSelectRelationship } from '~/components/ValueEditorSelectRelationship/ValueEditorSelectRelationship';

const NAMESPACE_MAP = {
  [SupplierFilterField.id]: CuriosityNamespaceType.SUPPLIER,
  [SupplierFilterField.sourceSystemId]: CuriosityNamespaceType.SOURCE_SYSTEM,
  [SupplierFilterField.commodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
  [SupplierFilterField.orderedProductId]: CuriosityNamespaceType.COMMODITY,
  [SupplierFilterField.orderedServiceId]: CuriosityNamespaceType.COMMODITY,
  [SupplierFilterField.industryId]: CuriosityNamespaceType.STD_NACE_CLASS,
  [SupplierFilterField.prequalificationTradeId]: CuriosityNamespaceType.PREQUALIFICATION_TRADE_NAME,
};

const prequalificationLevelOptions = Object.keys(PrequalificationLevelSelections);

export const useSupplierDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'Base.supplier' });
    return {
      fields: [
        // ---------- SUPPLIER INFO ----------
        createField({
          name: SupplierFilterField.id,
          grp: SupplierQueryFieldGroup.supplier,
          operators: operators.id,
        }),
        createField({
          name: SupplierFilterField.externalId,
          grp: SupplierQueryFieldGroup.supplier,
          operators: operators.text,
        }),
        createField({
          name: SupplierFilterField.employeeCount,
          grp: SupplierQueryFieldGroup.supplier,
          operators: operators.num,
        }),
        createField({
          name: SupplierFilterField.industryId,
          grp: SupplierQueryFieldGroup.supplier,
          operators: operators.id,
        }),
        createField({
          name: SupplierFilterField.legalType,
          grp: SupplierQueryFieldGroup.supplier,
          operators: operators.textAuto,
        }),
        createField({
          name: SupplierFilterField.relationship,
          grp: SupplierQueryFieldGroup.supplier,
          operators: operators.single,
        }),
        createField({
          name: SupplierFilterField.prequalificationLevel,
          grp: SupplierQueryFieldGroup.supplier,
          operators: operators.single,
        }),
        createField({
          name: SupplierFilterField.prequalificationTradeId,
          grp: SupplierQueryFieldGroup.supplier,
          operators: operators.id,
        }),
        createField({
          name: SupplierFilterField.tagCount,
          grp: SupplierQueryFieldGroup.supplier,
          operators: operators.num,
        }),
        // ---------- CONTRACTS ----------
        createField({
          name: SupplierFilterField.activeContractCount,
          grp: SupplierQueryFieldGroup.contract,
          operators: operators.num,
        }),
        // ---------- PRODUCTS ----------
        createField({
          name: SupplierFilterField.orderedProductId,
          grp: SupplierQueryFieldGroup.product,
          operators: operators.id,
        }),

        // ---------- SERVICES ----------
        createField({
          name: SupplierFilterField.orderedServiceId,
          grp: SupplierQueryFieldGroup.service,
          operators: operators.id,
        }),
        // ---------- ORDERS ----------
        createField({
          name: SupplierFilterField.orderLastDate,
          grp: SupplierQueryFieldGroup.order,
          operators: operators.date,
        }),
        createField({
          name: SupplierFilterField.orderVolumeYear,
          grp: SupplierQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: SupplierFilterField.orderCountYear,
          grp: SupplierQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: SupplierFilterField.orderVolume,
          grp: SupplierQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: SupplierFilterField.orderCount,
          grp: SupplierQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: SupplierFilterField.orderVolumeShare,
          grp: SupplierQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: SupplierFilterField.orderVolumeShareYear,
          grp: SupplierQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: SupplierFilterField.orderVolumeCumulative,
          grp: SupplierQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: SupplierFilterField.orderVolumeCumulativeYear,
          grp: SupplierQueryFieldGroup.order,
          operators: operators.num,
        }),
        // ---------- FINANCIALS ----------
        createField({
          name: SupplierFilterField.revenue,
          grp: SupplierQueryFieldGroup.financial,
          operators: operators.num,
        }),
        createField({
          name: SupplierFilterField.ebit,
          grp: SupplierQueryFieldGroup.financial,
          operators: operators.num,
        }),
        createField({
          name: SupplierFilterField.peRatio,
          grp: SupplierQueryFieldGroup.financial,
          operators: operators.num,
        }),
        // ---------- LOCATION ----------
        createField({
          name: SupplierFilterField.addressCountry,
          grp: SupplierQueryFieldGroup.location,
          operators: operators.idAuto,
        }),
        createField({
          name: SupplierFilterField.addressCity,
          grp: SupplierQueryFieldGroup.location,
          operators: operators.textAuto,
        }),
        createField({
          name: SupplierFilterField.locationRadius,
          grp: SupplierQueryFieldGroup.location,
          operators: operators.single,
        }),
        // ---------- COMMODITY GROUP ----------

        createField({
          name: SupplierFilterField.commodityGroupId,
          grp: SupplierQueryFieldGroup.commodityGroup,
          operators: operators.textAuto,
        }),
        // ---------- SOURCE SYSTEM ----------
        createField({
          name: SupplierFilterField.sourceSystemId,
          grp: SupplierQueryFieldGroup.sourceSystem,
          operators: operators.textAuto,
        }),
      ],
      getOperatorLabel: ({ field, operator }) => {
        switch (field) {
          case SupplierFilterField.lastContractDate:
          case SupplierFilterField.orderLastDate:
            if (operator === Operator.LT || operator === Operator.GT) {
              return `QueryBuilder.operatorDate.${operator}`;
            }
        }
      },
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          case SupplierFilterField.prequalificationTradeId:
          case SupplierFilterField.id:
          case SupplierFilterField.industryId:
          case SupplierFilterField.commodityGroupId:
          case SupplierFilterField.sourceSystemId:
          case SupplierFilterField.orderedProductId:
          case SupplierFilterField.orderedServiceId:
            return (
              <CuriosityAutocomplete
                ns={NAMESPACE_MAP[field]}
                multiple={listOperators[operator]}
                simplifyValue
                {...otherProps}
              />
            );
          case SupplierFilterField.prequalificationLevel:
            return (
              <ValueEditorSelectAutocomplete
                {...otherProps}
                initialI18nPrefix="PrequalificationLevelSelections"
                initialOptions={prequalificationLevelOptions}
                multiple={listOperators[operator]}
              />
            );
          case SupplierFilterField.legalType:
          case SupplierFilterField.addressCity:
          case SupplierFilterField.addressCountry:
          case SupplierFilterField.externalId:
            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple={true} freeSolo />;
            }
            return <ValueEditorInput {...otherProps} />;

          //
          case SupplierFilterField.orderVolumeShare:
          case SupplierFilterField.orderVolumeShareYear:
          case SupplierFilterField.orderVolumeCumulative:
          case SupplierFilterField.orderVolumeCumulativeYear:
          case SupplierFilterField.peRatio:
            return renderPercentageEditor({ operator, otherProps });
          //
          case SupplierFilterField.employeeCount:
          case SupplierFilterField.activeContractCount:
          case SupplierFilterField.tagCount:
          case SupplierFilterField.orderVolumeYear:
          case SupplierFilterField.orderCountYear:
          case SupplierFilterField.orderVolume:
          case SupplierFilterField.orderCount:
          case SupplierFilterField.revenue:
          case SupplierFilterField.ebit:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          //
          case SupplierFilterField.lastContractDate:
          case SupplierFilterField.orderLastDate:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorDateRange {...otherProps} />;
            }
            return <ValueEditorDate {...otherProps} />;
          case SupplierFilterField.locationRadius:
            return <LocationValueAutocomplete {...otherProps} />;
          case SupplierFilterField.relationship:
            return <ValueEditorSelectRelationship operator={operator} {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
